import { parseArtifactPayload } from 'breeze-engine-core';
import { convertFieldValueAvroToJson } from 'rpc-client-utils';
import { isFileArtifactTool, isArtifactsPath, parseArtifactFilename, deriveArtifactAction } from './artifactFilename';
import { isToolUseBlock } from './toolPairing';
function isAvroFieldValue(value) {
  if (typeof value !== 'object' || value === null) return false;
  const typename = value.__typename;
  return typeof typename === 'string' && typename.startsWith('com.hubspot.chirp.ext.models.');
}
function resolveInputRecord(input) {
  if (input == null) return null;
  const resolved = isAvroFieldValue(input) ? convertFieldValueAvroToJson(input) : input;
  if (resolved == null || typeof resolved !== 'object') return null;
  return resolved;
}
function getFilePath(record) {
  const candidates = ['filePath', 'file_path', 'path', 'filename', 'fileName'];
  for (const key of candidates) {
    const value = record[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
}
function getFileContent(record) {
  const value = record.content;
  return typeof value === 'string' ? value : null;
}

// Payload-bearing artifact types store a JSON document in the file body whose
// shape matches the corresponding ArtifactPayload. The artifact type is derived
// from the filename, so inject it as the `kind` discriminator that
// parseArtifactPayload dispatches on rather than trusting the file to carry it.
function parsePayloadFromContent(artifactType, content) {
  if (content === null) return undefined;
  let parsed;
  try {
    parsed = JSON.parse(content);
  } catch (_unused) {
    return undefined;
  }
  if (typeof parsed !== 'object' || parsed === null) return undefined;
  return parseArtifactPayload(Object.assign({}, parsed, {
    kind: artifactType
  }));
}
export function extractArtifactToolCallsFromBlocks(blocks) {
  const detected = [];
  for (const block of blocks) {
    if (!isToolUseBlock(block)) continue;
    const toolUse = block;
    if (!isFileArtifactTool(toolUse.name)) continue;
    const record = resolveInputRecord(toolUse.input);
    if (record === null) continue;
    const filePath = getFilePath(record);
    if (filePath === null) continue;
    if (!isArtifactsPath(filePath)) continue;
    detected.push({
      path: filePath,
      toolName: toolUse.name,
      toolUseId: toolUse.id,
      content: getFileContent(record)
    });
  }
  return detected;
}
export function extractArtifactToolCalls(pairs) {
  const detected = [];
  for (const pair of pairs) {
    if (!isFileArtifactTool(pair.input.name)) continue;
    const record = resolveInputRecord(pair.input.input);
    if (record === null) continue;
    const filePath = getFilePath(record);
    if (filePath === null) continue;
    if (!isArtifactsPath(filePath)) continue;
    detected.push({
      path: filePath,
      toolName: pair.input.name,
      toolUseId: pair.input.id,
      content: getFileContent(record)
    });
  }
  return detected;
}
export function buildArtifactBlocksFromDetected(detected) {
  if (detected.length === 0) return [];
  const byPath = new Map();
  for (const item of detected) {
    byPath.set(item.path, item);
  }
  const blocks = [];
  for (const item of byPath.values()) {
    const parsed = parseArtifactFilename(item.path);
    if (parsed === null) continue;
    const payload = parsePayloadFromContent(parsed.dataType, item.content);
    blocks.push(Object.assign({
      type: 'ARTIFACT',
      artifactType: parsed.dataType,
      name: parsed.title,
      path: item.path,
      action: deriveArtifactAction(item.toolName),
      source: 'filesystem'
    }, payload !== undefined && {
      payload
    }));
  }
  return blocks;
}
export function buildArtifactBlocksFromToolCalls(pairs) {
  return buildArtifactBlocksFromDetected(extractArtifactToolCalls(pairs));
}